import type { SeoLandingConfig } from "../types";

// Fatos com fonte em .claude/skills/seo/reference/fidexa-empresa.md
// (siglas: PEGN, ERP = Portal ERP, NV = Novo Varejo, BAG = Baguete).
// Não há índice de inadimplência só de vestuário na base: a dor usa o
// tamanho do mercado (IEMI), o peso do crediário (GMattos) e a inadimplência
// das lojas ao começar na Fidexa (BAG).
const SOURCE_ERP_NV = "Portal ERP e Novo Varejo, ago/2026";

export const crediarioLojaDeRoupas: SeoLandingConfig = {
  path: "/crediario-loja-de-roupas",
  kind: "segmento",
  keyword: "crediário para loja de roupas",
  title: "Crediário para loja de roupas: cobrança e Pix",
  description:
    "Crediário para loja de roupas e multimarcas: aviso automático da parcela no WhatsApp, Pix na conversa com baixa automática e o atraso negociado num chat só.",
  breadcrumbLabel: "Crediário para loja de roupas",
  updatedAt: "2026-10-17",
  service: {
    name: "Cobrança de crediário para lojas de roupas",
    serviceType: "Cobrança automatizada de crediário próprio via WhatsApp",
    audience:
      "Lojas de roupas, multimarcas e redes de moda com crediário próprio",
  },
  sections: [
    {
      type: "hero",
      eyebrow: "Para lojas de roupas e multimarcas",
      title:
        "Crediário para loja de roupas sem a equipe largar o provador para cobrar",
      lead: "Sua loja de moda vende no crediário próprio e a Fidexa cuida do resto: avisa a cliente da parcela, cobra quando ela atrasa e recebe por Pix no WhatsApp. Sua equipe só entra para negociar.",
      ctaLabel: "Agendar demonstração gratuita",
      secondaryLabel: "Entender o passo a passo",
      secondaryHref: "#como-funciona",
      reassurance: "Demonstração gratuita, com a carteira da sua loja.",
    },
    {
      type: "proof",
      title: "Entre as 249 empresas que cobram com a Fidexa:",
      clientNames: [
        "SuperModas",
        "Dantom Multimarcas",
        "Tangará",
        "Lojas Skala",
        "Lojas Rosário",
        "Sete 4 Sete",
      ],
    },
    {
      type: "shortAnswer",
      id: "resposta-curta",
      title: "O que muda no crediário da loja de roupas com a Fidexa",
      answer:
        "A loja de roupas continua vendendo no crediário próprio, mas deixa de cobrar na mão. A Fidexa recebe a carteira, manda pelo WhatsApp o aviso de cada vencimento, volta a cobrar quando a parcela atrasa e deixa a cliente quitar por Pix na conversa, com a baixa feita sozinha. Acordos ficam com a equipe, num chat só.",
      bullets: [
        "Serve para a loja única e para a rede multimarcas com várias unidades.",
        "Cobra parcelas de qualquer valor, até as pequenas.",
        "Não exige trocar o sistema de gestão da loja.",
      ],
    },
    {
      type: "pain",
      id: "dor-moda",
      title: "Na moda, o crediário pesa no caixa",
      lead: "Vender a peça da coleção parcelada no carnê traz cliente para a loja. O problema começa depois: pouca gente na equipe para acompanhar vencimento, mandar mensagem e conferir quem pagou.",
      facts: [
        {
          value: "R$ 396 bi",
          label: "Movimentados por vestuário e calçados no Brasil em 2025",
          source: `IEMI, citado por ${SOURCE_ERP_NV}`,
        },
        {
          value: "20% a 24%",
          label: "Parte da receita do varejo que passa pelo crediário",
          source: `GMattos, citado por ${SOURCE_ERP_NV}`,
        },
        {
          value: "15% a 30%",
          label:
            "Inadimplência anual das lojas no momento em que entram na Fidexa",
          source: "Fidexa, segundo o Baguete, set/2026",
        },
      ],
    },
    {
      type: "steps",
      id: "como-funciona",
      title: "Do carnê ao Pix: o crediário da loja de roupas no automático",
      steps: [
        {
          title: "Traga o crediário da loja",
          text: "Quem usa o TOTVS Linx liga a integração. Quem usa outro sistema envia uma planilha com as clientes, o valor de cada parcela e o vencimento.",
        },
        {
          title: "Aviso antes de vencer",
          text: "Cada cliente recebe no WhatsApp a lembrança da parcela antes da data e no próprio dia, em mensagens aprovadas pela Meta.",
        },
        {
          title: "Conversa no atraso",
          text: "Se a parcela passa da data, a régua manda a cobrança. Quando a cliente pede para parcelar, a equipe da loja fecha o acordo no chat da Fidexa.",
        },
        {
          title: "Pagou, baixou",
          text: "A cliente escolhe o que vai pagar e quita por Pix no WhatsApp. O pagamento entra registrado, sem conferência de extrato no fim do dia.",
        },
      ],
    },
    {
      type: "comparison",
      id: "comparacao",
      title: "Cobrar no balcão, pelo celular da loja ou com a Fidexa",
      lead: "Três jeitos de correr atrás da mesma parcela.",
      caption:
        "Comparação entre cobrar o crediário da loja de roupas no balcão, pelo WhatsApp do celular da loja e com a Fidexa",
      columns: [
        { key: "balcao", label: "Cobrança no balcão" },
        { key: "celular", label: "Vendedora no celular da loja" },
        { key: "fidexa", label: "Fidexa", highlight: true },
      ],
      rows: [
        {
          label: "Quem avisa a cliente do vencimento",
          cells: {
            balcao: "Ninguém, até ela voltar à loja",
            celular: "A vendedora, entre um atendimento e outro",
            fidexa: "Mensagem automática no WhatsApp",
          },
        },
        {
          label: "Cliente paga com a loja fechada",
          cells: {
            balcao: false,
            celular: "Só se alguém responder",
            fidexa: "Sim, pelo Fidexa Pay, 24 horas",
          },
        },
        {
          label: "Proposta de acordo para quem atrasou",
          cells: {
            balcao: "Na conversa de balcão",
            celular: "Caso a caso, à mão",
            fidexa: "A equipe da loja, no chat da Fidexa",
          },
        },
        {
          label: "Forma de pagamento",
          cells: {
            balcao: "No caixa da loja",
            celular: "Chave Pix enviada à mão",
            fidexa: "Pix dentro da conversa",
          },
        },
        {
          label: "Conferência do que foi pago",
          cells: {
            balcao: "Manual",
            celular: "Manual, olhando o extrato",
            fidexa: "Baixa automática",
          },
        },
        {
          label: "Rede multimarcas com várias unidades",
          cells: {
            balcao: "Cada loja por conta própria",
            celular: "Um celular por loja",
            fidexa: "Painel único, atendimento separado por loja",
          },
        },
      ],
    },
    {
      type: "metrics",
      id: "resultados",
      title: "O que a Fidexa já recuperou para o varejo",
      metrics: [
        {
          value: "R$ 39,34 mi",
          label: "Devolvidos ao caixa dos lojistas em menos de um ano",
          source: SOURCE_ERP_NV,
        },
        {
          value: "32,10%",
          label: "Taxa média de recuperação da carteira",
          source: "Plataforma Fidexa",
        },
        {
          value: "249",
          label: "Empresas cadastradas na plataforma",
          source: SOURCE_ERP_NV,
        },
      ],
    },
    {
      type: "calculator",
      id: "calculadora-moda",
      title: "Quanto do crediário da sua loja de roupas está parado?",
      text: "Informe a carteira e o atraso e veja quanto a inadimplência tira da loja por ano.",
      href: "/ferramentas/calculadora-de-inadimplencia",
      ctaLabel: "Fazer a conta",
    },
    {
      type: "faq",
      id: "faq",
      title: "Dúvidas sobre crediário para loja de roupas",
      items: [
        {
          question: "Compensa oferecer crediário próprio numa loja de roupas?",
          answer:
            "Compensa quando a loja consegue cobrar com regularidade. O crediário responde por 20% a 24% da receita do varejo, segundo a GMattos, e vestuário e calçados movimentaram R$ 396 bilhões em 2025, segundo o IEMI. O risco está nas parcelas que ninguém acompanha, e é essa parte que a Fidexa automatiza.",
        },
        {
          question: "Qual é a inadimplência de uma loja de roupas?",
          answer:
            "Depende da loja e da política de crédito, e as fontes que a Fidexa usa não trazem um índice só de vestuário. O que a Fidexa observa é que as lojas chegam à plataforma com inadimplência entre 15% e 30% ao ano, podendo chegar a 50% em alguns meses, segundo o Baguete.",
        },
        {
          question:
            "Como cobrar a cliente da loja de moda sem perder a cliente?",
          answer:
            "Com aviso antes do vencimento e mensagens respeitosas depois. Muita parcela atrasa por esquecimento, então o lembrete resolve boa parte. No atraso, a régua cobra sozinha e, se a cliente quiser negociar, quem conversa é a equipe da própria loja, no chat da Fidexa. Sem constrangimento, ela continua voltando à loja a cada coleção.",
        },
        {
          question: "Funciona para uma rede multimarcas com várias unidades?",
          answer:
            "Sim. As carteiras de todas as unidades ficam num painel único, e o atendimento no WhatsApp continua separado por loja. A Dantom Multimarcas está entre as empresas que usam a Fidexa.",
        },
        {
          question:
            "A vendedora precisa parar o atendimento para cobrar parcela?",
          answer:
            "Não. Os avisos saem sozinhos pela régua e a cliente paga pelo Fidexa Pay a qualquer hora, então a equipe fica livre para o salão, o provador e a vitrine. Ela só entra quando alguém atrasado quer negociar, pelo chat da Fidexa.",
        },
        {
          question: "Minha loja de roupas não usa Linx. A Fidexa funciona?",
          answer:
            "Funciona. A integração por API é com o ERP TOTVS Linx. Com outros sistemas, a carteira entra por planilha com as clientes, os valores devidos e as datas de vencimento, sem trocar o sistema da loja.",
        },
        {
          question: "Vale cobrar parcela pequena de uma peça de roupa?",
          answer:
            "Vale. A Fidexa cobra dívidas de qualquer valor, inclusive as de R$ 50 ou menos, justamente as que costumam ficar esquecidas porque levar à Justiça custaria mais do que a própria parcela.",
        },
        {
          question: "Como a cliente paga a parcela do crediário?",
          answer:
            "Por Pix, no WhatsApp. Pelo Fidexa Pay, ela recebe a mensagem, escolhe a parcela e paga na própria conversa. A Fidexa registra o pagamento e dá a baixa.",
        },
        {
          question: "E as clientes que já foram negativadas?",
          answer:
            "Também dá para cobrar. A Fidexa é usada para recuperar dívidas já encaminhadas aos órgãos de proteção ao crédito, inclusive as mais antigas da carteira.",
        },
        {
          question: "O número de WhatsApp da loja pode ser bloqueado?",
          answer:
            "A Fidexa envia pela API oficial do WhatsApp, com mensagens no modelo de template aprovado pela Meta, e não pelo aplicativo comum do celular da loja.",
        },
        {
          question: "Quanto custa a Fidexa para uma loja de roupas?",
          answer:
            "O valor depende do tamanho da operação. Na demonstração gratuita, um especialista mostra a Fidexa com a carteira da sua loja e apresenta a proposta.",
        },
      ],
    },
    {
      type: "finalCta",
      id: "fechamento",
      title: "Sua loja vende, a Fidexa cobra",
      text: "Agende uma demonstração gratuita e veja o crediário da sua loja de roupas sendo cobrado no WhatsApp.",
      ctaLabel: "Agendar demonstração gratuita",
    },
  ],
};
